<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { usePageBodyStyle } from '@/common/customisation/composables';
import { PUI } from '@pleased-npm/fe-customisation';
import { useWidgetConversation } from '@/stores/useWidgetConversation';
import { ExternalBotEvent } from '@/common/bot/enum';
import { randomId } from '@/library/message/lib';

import { useHomePageConfig } from './composables/useHomePageConfig';
import { useResumableChat } from './composables/useResumableChat';

import HomeHeader from './components/HomeHeader.vue';
import WelcomeMessage from './components/WelcomeMessage.vue';
import RequestSolvedRow from './components/RequestSolvedRow.vue';
import PageNavigationRow from './components/PageNavigationRow.vue';
import ArticleRow from './components/ArticleRow.vue';
import ConfirmResumableChat from '@/components/ConfirmResumableChat/ConfirmResumableChat.vue';
import { InteractionView, useChatWidget } from '@/stores/useChatWidget';
import { useBotUser } from '@/stores/useBotUser';
import { storeToRefs } from 'pinia';

const { contentStyle } = usePageBodyStyle({
  registryId: PUI.REGISTRY_IDS.HOME,
  sectionKey: PUI.HOME_SECTION_KEYS.BODY_BACKGROUND,
  blockKey: PUI.HOME_BLOCK_KEYS.BODY_SECTION,
});

const { navigationLinks, articles } = useHomePageConfig();

const userStore = useBotUser();
const { isAuthenticated } = storeToRefs(userStore);

const { setIsChatOverlayVisible, setInteractionView } = useChatWidget();

// Get submitFlowExternalAction from widget conversation store
const { submitFlowExternalAction, startFlow, openLandingOverlay } = useWidgetConversation();

// TODO: move modal + composable to separate component, which will be in App.vue
// Resumable chat logic
const {
  showModal,
  currentOpenChat,
  checkOpenChats,
  handleResume,
  handleEndChat,
  handleActiveConversation,
  handleMyRequests,
  handleNewChat,
} = useResumableChat({
  onResume: (chat, canBeResumed) => {
    setIsChatOverlayVisible(true);
    setInteractionView(InteractionView.LIVE_CHAT_INDICATOR);

    // Submit external event to resume the live chat
    submitFlowExternalAction({
      type: ExternalBotEvent.RESUME_LIVE_CHAT,
      id: randomId(),
      payload: {
        chatId: chat.id,
        firebaseToken: chat.firebaseToken,
        timeSinceTriggers: canBeResumed.timeSinceTriggers,
      },
    });
  },
  onNewChat: () => {
    openLandingOverlay();
  },
  onEndChat: () => {
    startFlow();
  },
});

const handleOpenWidget = () => {
  openLandingOverlay();
};

// Check for open chats on mount
watch(
  isAuthenticated,
  () => {
    if (isAuthenticated.value) {
      checkOpenChats();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="page-container">
    <HomeHeader>
      <WelcomeMessage />
    </HomeHeader>

    <!-- Content Container - Scrollable -->
    <div class="content-container page-content" :style="contentStyle">
      <!-- Request Solved Row - overlaps header -->
      <RequestSolvedRow class="request-solved-overlap" />

      <!-- Page Navigation Rows -->
      <div class="navigation-section">
        <PageNavigationRow
          v-for="link in navigationLinks"
          :key="link.sectionKey"
          :section-key="link.sectionKey"
          @open-widget="handleOpenWidget"
        />
      </div>

      <!-- Featured Articles -->
      <div>
        <ArticleRow v-for="article in articles" :key="article.sectionKey" :section-key="article.sectionKey" />
      </div>
    </div>

    <!-- Resumable Chat Modal -->
    <ConfirmResumableChat
      v-if="showModal && currentOpenChat"
      :resumable-chat="currentOpenChat"
      @resume="handleResume"
      @cancel="handleEndChat"
      @active-conversation="handleActiveConversation"
      @my-requests="handleMyRequests"
      @new-chat="handleNewChat"
    />
  </div>
</template>

<style scoped lang="scss">
.content-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  padding-bottom: 100px;
}

.navigation-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
